import { Component, computed, inject, Injector, input } from '@angular/core';
import {
  DocLineDeletedDto,
  DocLineMovedDto,
  DocLineRole,
  DocLineUpdatedDto,
  StockDocTransferDto,
  StockDocTransferService,
} from '@digiwall/goliath-documents/proxy';
import StockDocLinesEditorComponent, {
  StockDocLinesImports,
  StockDocLinesProviders,
} from '../../../stock-doc-lines-editor.component';
import { CellValueChangedEvent, ColDef } from '@ag-grid-community/core';
import { Observable, of } from 'rxjs';
import { LocalizationService } from '@abp/ng.core';
import { ObserverQueue, ShortcutKey } from '@digiwall/core';
import { StorageLocationService } from '@digiwall/goliath-core/proxy';
import { StockDocLineEditorHelperService } from '../../../../../components';
import { Router } from '@angular/router';
import { LabelPrinterHelper } from '@digiwall/goliath-products';
import {
  StockDocMovementPolicy,
  StockLinePolicy,
} from '../../../stock-doc-movements/stock-doc-movement-detail/stock-doc-movement-lines/stock-doc-movement-lines-policies/stock-doc-movement-line.policy';
import { StockDocEntryMovementPolicy } from '../../../stock-doc-movements/stock-doc-movement-detail/stock-doc-movement-lines/stock-doc-movement-lines-policies/stock-doc-entry-movement-line.policy';
import { StockDocWithdrawalMovementPolicy } from '../../../stock-doc-movements/stock-doc-movement-detail/stock-doc-movement-lines/stock-doc-movement-lines-policies/stock-doc-withdrawal-movement-line.policy';
import { ArticleSerialService, ArticleService } from '@digiwall/goliath-products/proxy';

@Component({
  selector: 'document-stock-doc-transfer-lines-grid',
  templateUrl: './stock-doc-transfer-lines-editor.component.html',
  imports: StockDocLinesImports,
  providers: StockDocLinesProviders,
})
export default class StockDocTransferLinesEditorComponent extends StockDocLinesEditorComponent<
  StockDocTransferDto,
  StockDocTransferService
> {
  public readonly copyLineData = input.required<boolean>();
  public readonly printLabel = input.required<boolean>();

  private labelPrinterHelper = inject(LabelPrinterHelper);

  private _policy!: StockLinePolicy;

  private getPolicy(): StockLinePolicy {
    return this._policy;
  }

  protected override get articleLineRole() {
    return DocLineRole.StockTransferArticleLine;
  }
  public get stockLocalizationService() {
    return this.localizationService;
  }
  public get stockArticleService(): ArticleService {
    return this.articleService;
  }
  public get stockArticleSerialService(): ArticleSerialService {
    return this.articleSerialService;
  }
  public get stockLabelPrinterHelper() {
    return this.labelPrinterHelper;
  }
  public get stockColDefs(): ColDef[] {
    return this.colDefs;
  }
  public stockIsBusyAdding(value: boolean): void {
    this._isBusyAdding.set(value);
  }

  constructor(
    localizationService: LocalizationService,
    docService: StockDocTransferService,
    storageLocationService: StorageLocationService,
    injector: Injector,
    editorHelper: StockDocLineEditorHelperService<
      DocLineUpdatedDto,
      DocLineMovedDto,
      DocLineDeletedDto
    >,
    updateQueue: ObserverQueue,
  ) {
    super(
      localizationService,
      docService,
      storageLocationService,
      injector,
      editorHelper,
      updateQueue,
    );
  }

  protected override initColDefs(): ColDef<any, any>[] {
    if (!this._policy) {
      this.setLinePolicy();
    }
    const colDefs = super.initColDefs();

    colDefs.push(this.editorHelper.colDef_LocationTo);
    colDefs.find(cd => cd.field === 'stockLocationId')!.headerName =
      this.localizationService.instant('Doc::StockDoc:StockLocationId');
    return this.getPolicy().enhanceColDefs(colDefs);
  }

  private setLinePolicy(): void {
    if (!this._policy) {
      const router = inject(Router);
      const isEntry = router.url.split('/').includes('stock-entry');
      const isWithdrawal = router.url.split('/').includes('stock-withdrawal');

      if (isEntry) {
        this._policy = new StockDocEntryMovementPolicy(this);

        const parentBtns = this.addActionBtns;
        this.addActionBtns = computed(() => {
          const btns = parentBtns();
          btns[0].shortcutKey = ShortcutKey.controlAdd;
          return btns;
        });
      } else if (isWithdrawal) {
        this._policy = new StockDocWithdrawalMovementPolicy(this);
      } else {
        this._policy = new StockDocMovementPolicy(this);
      }
    }
  }

  protected override getCellValueChangedObservable(e: CellValueChangedEvent): Observable<any> {
    const fromPolicy = this.getPolicy().onCellValueChanged(e);
    if (fromPolicy === true) {
      return super.getCellValueChangedObservable(e);
    }

    return fromPolicy instanceof Observable ? fromPolicy : of(fromPolicy);
  }

  protected override addArticleLine(lineFromId?: string, reference?: number): void {
    if (this.lineGrid.grid.rowData.length == 0) {
      super.addLine(this.getPolicy().getAddArticleLineFn(), lineFromId, reference);
      return;
    }

    this.getPolicy()
      .beforeAddArticleLine()
      .subscribeOnce(res => {
        if (res) {
          this.updateLines(res);
        }

        super.addLine(this.getPolicy().getAddArticleLineFn(), lineFromId, reference);
      });
  }

  public beforeSaveDocument(): Observable<DocLineUpdatedDto> {
    return this.getPolicy().beforeSaveDoc();
  }

  protected override onLineAdded(line: DocLineUpdatedDto, setFocus: boolean = true): void {
    super.onLineAdded(line, this.getPolicy().focusOnNewLine());
    this.getPolicy().onLineAdded();
  }
}
